import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, ShieldCheck, Target } from "lucide-react";

/**
 * Rich right-side hero card — showcases the current top-confidence
 * prediction so the wide-screen hero fills the horizontal space with
 * actual product value instead of empty gradient.
 */
export const HeroTopPickCard = ({ prediction }) => {
  const match = prediction?.match || {};
  const top = (prediction?.top_predictions || [])[0] || {};
  const home = match?.home_team?.name || "—";
  const away = match?.away_team?.name || "—";
  const league = match?.league?.name || "Featured Match";
  const country = match?.league?.country || "";
  const homeLogo = match?.home_team?.logo;
  const awayLogo = match?.away_team?.logo;
  const conf = typeof top.confidence === "number"
    ? Math.round(top.confidence * (top.confidence <= 1 ? 100 : 1))
    : null;
  const market = top.market_type || "1X2";
  const pick = top.prediction || top.pick || "—";
  const fid = match?.fixture_id || match?.id;
  const kickoff = match?.date;
  const kickoffLabel = kickoff
    ? new Date(kickoff).toLocaleString([], {
        weekday: "short", hour: "2-digit", minute: "2-digit",
      })
    : "";

  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-border/70 bg-gradient-to-br from-card via-card to-muted/40 p-6 lg:p-7 shadow-[0_28px_80px_-32px_rgba(0,0,0,0.55)]"
      data-testid="hero-top-pick-card"
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/15 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl"
      />

      <div className="relative flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
        <Zap className="w-3.5 h-3.5" />
        Top confidence · Live pick
      </div>

      <div className="relative mt-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
        <span className="truncate">{league}</span>
        {country && <span className="text-muted-foreground/60">· {country}</span>}
      </div>

      <div className="relative mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className="flex flex-col items-start gap-2 min-w-0">
          {homeLogo ? (
            <img
              src={homeLogo}
              alt=""
              className="h-12 w-12 rounded-full bg-background/70 border border-border object-contain p-1"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
              {home.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="text-sm font-semibold text-foreground truncate max-w-full" title={home}>
            {home}
          </div>
        </div>
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">vs</span>
          <span className="text-[10px] text-muted-foreground/80">{kickoffLabel}</span>
        </div>
        <div className="flex flex-col items-end gap-2 min-w-0">
          {awayLogo ? (
            <img
              src={awayLogo}
              alt=""
              className="h-12 w-12 rounded-full bg-background/70 border border-border object-contain p-1"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
              {away.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="text-sm font-semibold text-foreground truncate max-w-full text-right" title={away}>
            {away}
          </div>
        </div>
      </div>

      <div className="relative mt-6 grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-border/70 bg-background/50 p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <Target className="w-3 h-3" /> Model pick
          </div>
          <div className="mt-1.5 text-sm font-bold text-foreground truncate">{pick}</div>
          <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{market}</div>
        </div>
        <div className="rounded-2xl border border-primary/40 bg-primary/10 p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-primary">
            <ShieldCheck className="w-3 h-3" /> Confidence
          </div>
          <div className="mt-1.5 font-display text-3xl font-bold text-primary leading-none">
            {conf !== null ? `${conf}%` : "—"}
          </div>
          <div className="mt-1 h-1.5 bg-background/60 rounded overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${conf !== null ? Math.min(100, conf) : 0}%` }}
            />
          </div>
        </div>
      </div>

      {fid && (
        <Link
          to={`/matches/${fid}`}
          className="relative mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          data-testid="hero-top-pick-analyze-link"
        >
          Full analysis
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
};

/** Skeleton shown while the homepage still fetches predictions. */
export const HeroTopPickSkeleton = () => (
  <div
    className="relative overflow-hidden rounded-3xl border border-border/70 bg-card p-6 lg:p-7 min-h-[300px]"
    data-testid="hero-top-pick-skeleton"
  >
    <div className="animate-pulse space-y-4">
      <div className="h-3 w-40 rounded bg-muted" />
      <div className="h-3 w-24 rounded bg-muted/70" />
      <div className="flex items-center justify-between pt-4">
        <div className="flex flex-col gap-2">
          <div className="h-12 w-12 rounded-full bg-muted" />
          <div className="h-3 w-24 rounded bg-muted" />
        </div>
        <div className="h-3 w-8 rounded bg-muted/70" />
        <div className="flex flex-col gap-2 items-end">
          <div className="h-12 w-12 rounded-full bg-muted" />
          <div className="h-3 w-24 rounded bg-muted" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-4">
        <div className="h-20 rounded-2xl bg-muted" />
        <div className="h-20 rounded-2xl bg-muted" />
      </div>
    </div>
  </div>
);

export default HeroTopPickCard;
